import { useEffect, useRef, useState } from 'react';
import { useDebounceFn } from 'ahooks';
import { useFetcher } from '@remix-run/react';
import { EMAIL_REGEX } from '@modules/validation/util';
import { HDE_GUEST_EMAIL_ALREADY_REGISTERED_MESSAGE } from '../constants';
import { HDE_GUEST_EMAIL_CHECK_API_ROUTE } from '~/routes/remix-api.app.hde-guest-email-check';

interface GuestEmailCheckResponse {
    eventId?: number;
    guestEmail?: string;
    isRegistered: boolean;
    error?: string;
}

interface UseHdeGuestEmailCheckParams {
    eventId: number | null;
    guestEmail: string;
    /** When updating an existing submission, skip collision for its current guest email. */
    existingSubmissionId: string | null;
    /** Only run the lookup when the guest details form is visible. */
    enabled: boolean;
}

/**
 * Debounced lookup for whether a guest invite email is already registered on the event.
 * Returns an inline error message suitable for the guest email field.
 */
export function useHdeGuestEmailCheck({
    eventId,
    guestEmail,
    existingSubmissionId,
    enabled,
}: UseHdeGuestEmailCheckParams) {
    const fetcher = useFetcher<GuestEmailCheckResponse>();
    const loadRef = useRef(fetcher.load);
    loadRef.current = fetcher.load;
    const lastRequestKeyRef = useRef<string | null>(null);
    const [resolved, setResolved] = useState<{
        requestKey: string;
        isRegistered: boolean;
    } | null>(null);

    const trimmedEmail = guestEmail.trim();
    const emailFormatValid =
        trimmedEmail.length > 0 && EMAIL_REGEX.test(trimmedEmail);
    const active =
        enabled && Boolean(eventId) && (eventId ?? 0) > 0 && emailFormatValid;

    const { run: scheduleLookup, cancel: cancelLookup } = useDebounceFn(
        (requestKey: string, params: URLSearchParams) => {
            lastRequestKeyRef.current = requestKey;
            setResolved(null);
            loadRef.current(
                `${HDE_GUEST_EMAIL_CHECK_API_ROUTE}?${params.toString()}`,
            );
        },
        { wait: 400 },
    );

    useEffect(() => {
        if (!active || !eventId) {
            cancelLookup();
            lastRequestKeyRef.current = null;
            setResolved(null);
            return;
        }

        const params = new URLSearchParams({
            eventId: String(eventId),
            guestEmail: trimmedEmail,
        });
        if (existingSubmissionId) {
            params.set('excludeSubmissionId', existingSubmissionId);
        }

        const requestKey = `${eventId}:${trimmedEmail.toLowerCase()}:${existingSubmissionId ?? ''}`;
        scheduleLookup(requestKey, params);
    }, [
        active,
        eventId,
        trimmedEmail,
        existingSubmissionId,
        scheduleLookup,
        cancelLookup,
    ]);

    useEffect(() => {
        if (
            !lastRequestKeyRef.current ||
            fetcher.state !== 'idle' ||
            fetcher.data === undefined
        ) {
            return;
        }

        const responseEmail = fetcher.data.guestEmail?.trim().toLowerCase();
        if (responseEmail && responseEmail !== trimmedEmail.toLowerCase()) {
            return;
        }

        setResolved({
            requestKey: lastRequestKeyRef.current,
            isRegistered: fetcher.data.isRegistered,
        });
    }, [fetcher.state, fetcher.data, trimmedEmail]);

    const currentRequestKey =
        active && eventId
            ? `${eventId}:${trimmedEmail.toLowerCase()}:${existingSubmissionId ?? ''}`
            : null;
    const isResolved =
        currentRequestKey !== null &&
        resolved?.requestKey === currentRequestKey;
    const isChecking = active && !isResolved;

    const guestEmailRegisteredError =
        isResolved && resolved?.isRegistered
            ? HDE_GUEST_EMAIL_ALREADY_REGISTERED_MESSAGE
            : null;

    return {
        guestEmailRegisteredError,
        isCheckingGuestEmail: isChecking,
    };
}
